<script setup lang="ts">
import { computed, type CSSProperties, type PropType } from 'vue';
import { textProps } from 'naive-ui';
import { gaiaFontSize, gaiaFontWeight, gaiaTextTone, gaiaTracking } from '../themes/tokens';

type Tone = keyof typeof gaiaTextTone;
type Size = keyof typeof gaiaFontSize;
type Weight = keyof typeof gaiaFontWeight;

const props = defineProps({
  ...textProps,
  /**
   * Gaia text color. Prefer this over naive's `depth` (naive's own greys, not
   * the ink scale) and over `type="error"` (naive's red, not gaiaStatus.alert).
   */
  tone: {
    type: String as PropType<Tone>,
    default: undefined,
  },
  /**
   * Step on the semantic type scale. Undefined inherits the surrounding size,
   * so components name a role rather than writing a px font-size.
   */
  size: {
    type: String as PropType<Size>,
    default: undefined,
  },
  /**
   * naive's `strong` is a single weight and can't express the 600/700 split
   * this system uses. Set after `variant`, so it overrides `label`'s bold.
   */
  weight: {
    type: String as PropType<Weight>,
    default: undefined,
  },
  /**
   * `label` = the uppercase micro-label recipe. Case and tracking are coupled
   * — tracking is never used without uppercase — so they ship together, while
   * `size` and `weight` compose on top. The three call sites needing a
   * non-standard tracking pass it as a one-line `:style`, which wins over this.
   */
  variant: {
    type: String as PropType<'default' | 'label'>,
    default: 'default',
  },
  /**
   * Clip this run to one line with an ellipsis. Half the idiom lives here; the
   * other half is `min-width: 0` on the flex parent, without which the parent
   * refuses to shrink below its content and there is nothing to clip.
   *
   * `display: block` is part of the recipe because `overflow` does not apply to
   * inline elements — a no-op wherever this is already a flex or grid item
   * (those are blockified), and what makes the prop work anywhere else.
   */
  truncate: Boolean,
});

// tone/size/weight/variant/truncate are ours, not n-text's — keep them off the DOM.
const passThrough = computed(() => {
  const { tone, size, weight, variant, truncate, ...rest } = props;
  return rest;
});

const gaiaStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {};
  if (props.variant === 'label') {
    style.textTransform = 'uppercase';
    style.letterSpacing = gaiaTracking.label;
    style.fontWeight = gaiaFontWeight.bold;
  }
  if (props.tone) style.color = gaiaTextTone[props.tone];
  if (props.size) style.fontSize = gaiaFontSize[props.size];
  // After the variant, so an explicit weight wins over `label`'s bold.
  if (props.weight) style.fontWeight = gaiaFontWeight[props.weight];
  if (props.truncate) {
    style.display = 'block';
    style.overflow = 'hidden';
    style.textOverflow = 'ellipsis';
    style.whiteSpace = 'nowrap';
  }
  return style;
});
</script>

<template>
  <n-text v-bind="passThrough" :style="gaiaStyle">
    <slot />
  </n-text>
</template>
